// Copyright (c) 2026 Interpaste, all rights reserved
// @author: norelock <https://gitlab.com/mvvnlight>

import { YouTubePlayer, SoundCloudPlayer, LiveVideoPlayer, LiveAudioPlayer } from '../players'
import { ORIGIN } from '../env'
import { Logger } from '../utils'
import type { HostPayload, PlayerBridge, PlayerType } from '../types'

export class PlayerRuntimeController {
  private logger = Logger.create('runtime')

  private currentType: PlayerType | null = null
  private activePlayer: PlayerBridge | null = null
  private source: Window | null = null
  private origin: string | null = null

  private lastLoadKey = ''
  private lastLoadAt = 0
  private readonly loadDebounceMs = 300

  onMessage(event: MessageEvent): void {
    const msg = event.data

    if (typeof msg === 'string') {
      if (this.activePlayer?.handleCommand(msg)) return

      try {
        const parsed = JSON.parse(msg) as Record<string, unknown>
        if (this.handleObject(parsed, event)) return
      } catch {
        // Ignore non-json messages
      }
      return
    }

    if (typeof msg === 'object' && msg !== null) {
      this.handleObject(msg as Record<string, unknown>, event)
    }
  }

  private handleObject(parsed: Record<string, unknown>, event: MessageEvent): boolean {
    if (this.handleHostPayload(parsed, event)) {
      return true
    }

    return this.handleYouTubeEvent(parsed)
  }

  private postToHost(message: string): void {
    if (!this.source || !this.origin) return
    this.source.postMessage(message, this.origin)
  }

  private getOrCreatePlayer(type: PlayerType): PlayerBridge {
    if (this.currentType === type && this.activePlayer) {
      return this.activePlayer
    }

    if (this.activePlayer) {
      this.activePlayer.destroy()
      this.activePlayer = null
    }

    this.currentType = type

    switch (type) {
      case 'youtube':
        this.activePlayer = new YouTubePlayer((message) => this.postToHost(message))
        return this.activePlayer
      case 'soundcloud':
        this.activePlayer = new SoundCloudPlayer((message) => this.postToHost(message))
        return this.activePlayer
      case 'hls':
        this.activePlayer = new LiveVideoPlayer('hls', (message) => this.postToHost(message))
        return this.activePlayer
      case 'mp4':
        this.activePlayer = new LiveVideoPlayer('mp4', (message) => this.postToHost(message))
        return this.activePlayer
      case 'mp3':
        this.activePlayer = new LiveAudioPlayer((message) => this.postToHost(message))
        return this.activePlayer
      default:
        this.activePlayer = new YouTubePlayer((message) => this.postToHost(message))
        return this.activePlayer
    }
  }

  private handleHostPayload(parsed: Record<string, unknown>, event: MessageEvent): boolean {
    if (typeof parsed.volume === 'undefined') return false

    const data = parsed as unknown as HostPayload
    if (!data.type) data.type = 'youtube'

    const loadKey = `${data.type}:${data.id}:${data.seek}`
    const now = Date.now()
    if (loadKey === this.lastLoadKey && now - this.lastLoadAt < this.loadDebounceMs) {
      this.logger.debug(`Debounced duplicate load: ${loadKey}`)
      return true
    }

    this.lastLoadKey = loadKey
    this.lastLoadAt = now

    this.source = event.source as Window
    this.origin = event.origin === 'null' ? ORIGIN : event.origin

    const player = this.getOrCreatePlayer(data.type)
    player.load(data)

    this.logger.debug(`Loaded ${data.type} player`, JSON.stringify(data))
    return true
  }

  private handleYouTubeEvent(parsed: Record<string, unknown>): boolean {
    if (this.currentType !== 'youtube') return false
    const yt = this.activePlayer as YouTubePlayer | null
    return yt?.handleYouTubeEvent(parsed) ?? false
  }
}
